import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle2, AlertCircle, Info, TriangleAlert, X } from 'lucide-react';
import { cn } from '../utils/cn';

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  /** Label rendered on the inline action button. */
  label: string;
  /**
   * Called when the user clicks the action. The toast is
   * auto-dismissed immediately after — pass a navigate() call or a
   * one-shot side-effect here.
   */
  onClick: () => void;
}

export interface ToastItem {
  id: string;
  kind: ToastKind;
  title: string;
  description?: string;
  duration?: number;
  /**
   * Optional inline action (e.g. "View", "Undo"). When present, the
   * button is rendered on the toast body; clicking it invokes the
   * handler and dismisses the toast.
   */
  action?: ToastAction;
}

interface ToastCtx {
  show: (t: Omit<ToastItem, 'id'>) => string;
  dismiss: (id: string) => void;
  success: (title: string, description?: string) => string;
  error: (title: string, description?: string) => string;
  info: (title: string, description?: string) => string;
  warning: (title: string, description?: string) => string;
}

const Ctx = createContext<ToastCtx | null>(null);

const iconMap: Record<ToastKind, React.ReactNode> = {
  success: <CheckCircle2 className="h-4 w-4" />,
  error: <AlertCircle className="h-4 w-4" />,
  warning: <TriangleAlert className="h-4 w-4" />,
  info: <Info className="h-4 w-4" />,
};

const toneMap: Record<ToastKind, string> = {
  success: 'bg-success-light text-success border-success/40',
  error: 'bg-danger-light text-danger border-danger/40',
  warning: 'bg-warning-light text-warning border-warning/40',
  info: 'bg-info-light text-info border-info/40',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (t: Omit<ToastItem, 'id'>) => {
      const id = Math.random().toString(36).slice(2, 10);
      const item: ToastItem = { duration: 3500, ...t, id };
      setItems((prev) => [...prev, item]);
      const handle = window.setTimeout(() => dismiss(id), item.duration);
      timers.current.set(id, handle);
      return id;
    },
    [dismiss]
  );

  useEffect(() => () => {
    timers.current.forEach((h) => clearTimeout(h));
    timers.current.clear();
  }, []);

  const api: ToastCtx = {
    show,
    dismiss,
    success: (title, description) => show({ kind: 'success', title, description }),
    error: (title, description) => show({ kind: 'error', title, description }),
    info: (title, description) => show({ kind: 'info', title, description }),
    warning: (title, description) => show({ kind: 'warning', title, description }),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-80 flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2 rounded-md border p-3 text-sm shadow-lg backdrop-blur',
              toneMap[t.kind]
            )}
          >
            <span className="mt-0.5">{iconMap[t.kind]}</span>
            <div className="flex-1">
              <div className="font-semibold">{t.title}</div>
              {t.description && <div className="mt-0.5 text-xs opacity-90">{t.description}</div>}
              {t.action && (
                <button
                  type="button"
                  onClick={() => {
                    try {
                      t.action?.onClick();
                    } finally {
                      dismiss(t.id);
                    }
                  }}
                  className="mt-1.5 inline-flex items-center gap-1 rounded border border-current/40 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide hover:bg-black/10"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="rounded hover:bg-black/10 p-1"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast must be used within <ToastProvider>');
  return v;
}
